import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import BatchTranscribe from './BatchTranscribe';

/**
 * Step 0 – Video / audio invoer.
 */
function StepVideoInput({
  inputMethod,
  setInputMethod,
  // file upload
  convertingWav,
  wavConversionProgress,
  isVideoDragActive,
  getVideoRootProps,
  getVideoInputProps,
  videoFile,
  setVideoFile,
  videoInfo,
  setVideoInfo,
  analyzing,
  // frame.io link
  frameIoLink,
  setFrameIoLink,
  analyzeVideo,
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        📹 Video of audio uploaden
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          label="Upload bestand"
          color={inputMethod === 'upload' ? 'primary' : 'default'}
          onClick={() => setInputMethod('upload')}
          icon={<CloudUploadIcon />}
        />
        <Chip
          label="Frame.io link"
          color={inputMethod === 'link' ? 'primary' : 'default'}
          onClick={() => setInputMethod('link')}
          icon={<LinkIcon />}
        />
        <Chip
          label="Batch transcribe"
          color={inputMethod === 'batch' ? 'primary' : 'default'}
          onClick={() => setInputMethod('batch')}
          icon={<QueueMusicIcon />}
        />
      </Box>

      {inputMethod === 'batch' ? (
        <BatchTranscribe embedded />
      ) : inputMethod === 'upload' ? (
        <Paper
          {...(convertingWav ? {} : getVideoRootProps())}
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: convertingWav ? 'default' : 'pointer',
            backgroundColor: convertingWav
              ? '#fff3e0'
              : isVideoDragActive
              ? '#e3f2fd'
              : videoFile
              ? '#e8f5e9'
              : '#fafafa',
            border: '2px dashed',
            borderColor: convertingWav
              ? '#ff9800'
              : isVideoDragActive
              ? '#2196f3'
              : videoFile
              ? '#4caf50'
              : '#ccc',
            borderRadius: 2,
            transition: 'all 0.2s',
            '&:hover': convertingWav
              ? {}
              : {
                  borderColor: '#2196f3',
                  backgroundColor: '#e3f2fd',
                },
          }}
        >
          {!convertingWav && <input {...getVideoInputProps()} />}

          {convertingWav ? (
            <Box>
              <CircularProgress sx={{ color: '#ff9800', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#e65100' }}>
                🎵 WAV → MP3 Conversie
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {wavConversionProgress.message}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={wavConversionProgress.percent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#ffe0b2',
                  '& .MuiLinearProgress-bar': { backgroundColor: '#ff9800' },
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {wavConversionProgress.percent}% - Even geduld, grote WAV bestanden kunnen even duren
              </Typography>
            </Box>
          ) : videoFile ? (
            <Box>
              {analyzing ? (
                <>
                  <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#6366f1' }}>
                    {videoFile.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Bestand analyseren...
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                  <Typography variant="h6">{videoFile.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {((videoFile.serverSize || videoFile.size) / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                  <Tooltip title="Verwijder bestand">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setVideoFile(null);
                        setVideoInfo(null);
                      }}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
              <Typography variant="h6">
                {isVideoDragActive ? 'Drop hier!' : 'Sleep video/audio hierheen'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                of klik om te selecteren (MP4, MOV, MP3, WAV - max 500MB)
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                💡 Grote WAV bestanden worden automatisch naar MP3 geconverteerd
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Box>
          <TextField
            fullWidth
            label="Frame.io link"
            placeholder="https://app.frame.io/..."
            value={frameIoLink}
            onChange={(e) => {
              setFrameIoLink(e.target.value);
              setVideoFile(null);
              setVideoInfo(null);
            }}
            InputProps={{
              startAdornment: <LinkIcon sx={{ mr: 1, color: '#999' }} />,
            }}
          />
          {frameIoLink && !videoInfo && !analyzing && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoFixHighIcon />}
              onClick={() => analyzeVideo(null, frameIoLink)}
              sx={{ mt: 1 }}
            >
              Analyseer video
            </Button>
          )}
          {analyzing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Video analyseren...
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default StepVideoInput;
